import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import PrivateRoute from './components/common/PrivateRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import AppLayout from './components/common/AppLayout/AppLayout';
import GlobalSearchModal from './components/search/GlobalSearchModal';

// Auth pages (eagerly loaded)
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';

// Lazy-loaded pages
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage'));
const ProjectListPage = lazy(() => import('./components/projects/ProjectListPage'));
const ProjectSettingsPage = lazy(() => import('./components/projects/ProjectSettingsPage'));
const IssueListPage = lazy(() => import('./components/issues/IssueListPage'));
const IssueDetailPage = lazy(() => import('./components/issues/IssueDetailPage'));
const KanbanBoard = lazy(() => import('./components/boards/KanbanBoard'));
const BacklogPage = lazy(() => import('./components/backlog/BacklogPage'));
const SprintReportPage = lazy(() => import('./components/sprints/SprintReportPage'));
const ReleaseListPage = lazy(() => import('./components/releases/ReleaseListPage'));
const ReleaseDetailPage = lazy(() => import('./components/releases/ReleaseDetailPage'));
const SpaceListPage = lazy(() => import('./components/knowledge-base/SpaceListPage'));
const SpaceDetailPage = lazy(() => import('./components/knowledge-base/SpaceDetailPage'));
const PageViewPage = lazy(() => import('./components/knowledge-base/PageViewPage'));
const PageEditPage = lazy(() => import('./components/knowledge-base/PageEditPage'));
const ProfilePage = lazy(() => import('./components/profile/ProfilePage'));
const OrgSettingsPage = lazy(() => import('./components/admin/OrgSettingsPage'));
const RolesPage = lazy(() => import('./components/admin/RolesPage'));
const TeamsPage = lazy(() => import('./components/admin/TeamsPage'));
const AuditLogPage = lazy(() => import('./components/admin/AuditLogPage'));
const IssueTypesPage = lazy(() => import('./components/admin/IssueTypesPage'));
const LabelsPage = lazy(() => import('./components/admin/LabelsPage'));
const CustomFieldsPage = lazy(() => import('./components/admin/CustomFieldsPage'));
const NotificationDefaultsPage = lazy(() => import('./components/admin/NotificationDefaultsPage'));
const PublicPageView = lazy(() => import('./components/knowledge-base/PublicPageView'));
const SharedPageView = lazy(() => import('./components/knowledge-base/SharedPageView'));
const PublicReleaseNotes = lazy(() => import('./components/releases/PublicReleaseNotes'));
const ChatPage = lazy(() => import('./components/chat/ChatPage'));
const IntegrationsPage = lazy(() => import('./components/admin/IntegrationsPage'));
const ReportsPage = lazy(() => import('./components/admin/ReportsPage'));
const BlogListPage = lazy(() => import('./components/blog/BlogListPage'));
const BlogEditorPage = lazy(() => import('./components/blog/BlogEditorPage'));
const LeadListPage = lazy(() => import('./components/leads/LeadListPage'));
const AcceptInvitePage = lazy(() => import('./components/auth/AcceptInvitePage'));
const SsoCallbackPage = lazy(() => import('./components/auth/SsoCallbackPage'));

const App = () => {
  const { initialize, isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (isLoading) {
    return <LoadingSpinner tip="Loading MkraftOne..." />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <GlobalSearchModal />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
            />
            <Route
              path="/register"
              element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />}
            />

            {/* Public KB page (no auth required) */}
            <Route path="/public/kb/:shareToken" element={<PublicPageView />} />
            <Route path="/public/release/:shareToken" element={<PublicReleaseNotes />} />

            {/* Shared KB page (auth required, email-restricted) */}
            <Route path="/shared/kb/:shareToken" element={<SharedPageView />} />

            {/* SSO callback */}
            <Route path="/sso/callback" element={<SsoCallbackPage />} />

            {/* Invitation accept page */}
            <Route path="/invite/:token" element={<AcceptInvitePage />} />

            {/* Authenticated routes */}
            <Route element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }>
              <Route index element={<DashboardPage />} />

              {/* Projects */}
              <Route path="/projects" element={<ProjectListPage />} />
              <Route path="/projects/:projectId/settings" element={<ProjectSettingsPage />} />

              {/* Issues & Board */}
              <Route path="/projects/:projectId/board" element={<KanbanBoard />} />
              <Route path="/projects/:projectId/backlog" element={<BacklogPage />} />
              <Route path="/projects/:projectId/issues" element={<IssueListPage />} />
              <Route path="/projects/:projectId/issues/:issueKey" element={<IssueDetailPage />} />
              <Route path="/projects/:projectId/sprints/:sprintId/report" element={<SprintReportPage />} />
              <Route path="/projects/:projectId/releases" element={<ReleaseListPage />} />
              <Route path="/projects/:projectId/releases/:releaseId" element={<ReleaseDetailPage />} />
              <Route path="/spaces" element={<SpaceListPage />} />
              <Route path="/spaces/:spaceId" element={<SpaceDetailPage />} />
              <Route path="/spaces/:spaceId/pages/:pageId" element={<PageViewPage />} />
              <Route path="/spaces/:spaceId/pages/:pageId/edit" element={<PageEditPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin/organization" element={<OrgSettingsPage />} />
              <Route path="/admin/roles" element={<RolesPage />} />
              <Route path="/admin/teams" element={<TeamsPage />} />
              <Route path="/admin/issue-types" element={<IssueTypesPage />} />
              <Route path="/admin/labels" element={<LabelsPage />} />
              <Route path="/admin/custom-fields" element={<CustomFieldsPage />} />
              <Route path="/admin/notifications" element={<NotificationDefaultsPage />} />
              <Route path="/admin/audit" element={<AuditLogPage />} />
              <Route path="/admin/reports" element={<ReportsPage />} />
              <Route path="/admin/integrations" element={<IntegrationsPage />} />
              <Route path="/blogs" element={<BlogListPage />} />
              <Route path="/blogs/:blogId" element={<BlogEditorPage />} />
              <Route path="/leads" element={<LeadListPage />} />
              <Route path="/chat" element={<ChatPage />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
